import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef, ViewContainerRef, ViewChildren, QueryList, ViewChild } from "@angular/core";
import { SharedService } from '../shared';
import * as d3 from 'd3';
import Sunburst from 'sunburst-chart';
import * as d3Sankey from 'd3-sankey-diagram';
import { sumBy } from 'lodash';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DatenubersichtInfoComponent } from './datenubersicht-info';
import { SplitComponent, SplitAreaDirective } from 'angular-split';
import { getSunBurstChildrenData, getSankeyData } from '../../workers/datenubersicht.worker';
import { LANGUAGE_ENUM } from "../dateneingabe-frageboge/questionnaire.model";

declare var jsPanel: any;

@Component({
    selector: 'app-datenubersicht-common',
    template: "",
    encapsulation: ViewEncapsulation.None
})
export class DatenubersichtCommonComponent implements OnInit {
    @ViewChild(SplitComponent) splitEl: SplitComponent
    @ViewChildren(SplitAreaDirective) areasEl: QueryList<SplitAreaDirective>

    showLoader: boolean = false;
    dataList: any[] = [];
    chartData: any = {};

    LANGUAGE: LANGUAGE_ENUM = LANGUAGE_ENUM.DE;

    heatmapParameters: any[] = []

    ageGroupData: any[] = ["<21", "21-40", "41-65", ">65"];

    activeTab: 'SUNBURST' | 'SANKEY' = 'SUNBURST';

    jsPanelPopup: any;

    get panelWidth() {
        return Math.min(1100, window.innerWidth - 60);
    }

    get panelHeight() {
        return Math.min(800, window.innerHeight - 60);
    }

    constructor(protected cd: ChangeDetectorRef,
        protected vcr: ViewContainerRef,
        protected sharedService: SharedService) {
    }

    ngOnInit(): void {
        this.getHeatMapData();
    }
    onChangeTab(tab) {
        if (this.activeTab == tab) {
            return;
        }
        this.activeTab = tab;
        this.fnGenerateDiagram();
    }

    getHeatMapData() {
        this.showLoader = true;
        this.sharedService.getHeatMapENData().subscribe({
            next: (res) => {
                this.dataList = res;
                this.fnGenerateDiagram();
                this.showLoader = false;
            },
            error: (err) => {
                this.dataList = [];
                this.fnGenerateDiagram();
                this.showLoader = false;

            }
        })
    }


    isValueInArr(Arr, value) {
        if (!Arr) {
            Arr = [];
        }
        return Arr.indexOf(value) > -1;
    }

    getFormSVGColor(Arr, value) {
        return this.isValueInArr(Arr, value) ? "#D6ECFF" : "#F8F8F4";
    }

    fnToggleSelected(param: any) {
        param.selected = !param.selected;
    }

    fnDropParameter(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.heatmapParameters, event.previousIndex, event.currentIndex);
    }

    onSplitDragEnd(event) {
        this.onResizeWindow(event);
    }

    timeoutResize: any;
    onResizeWindow(event) {
        clearTimeout(this.timeoutResize);
        this.timeoutResize = setTimeout(() => {
            switch (this.activeTab) {
                case 'SUNBURST':
                    this.displaySunBurstChart();
                    break;
                case 'SANKEY':
                    this.displaySankeyChart();
                    break;
            }

        }, 500)
    }

    get selectedParameters() {
        return this.heatmapParameters.filter(a => a.selected).map(a => a.label);
    }

    private renderTimeout: any;
    fnGenerateDiagram() {
        document.getElementById('chart').innerHTML = "";
        this.showLoader = true;
        clearTimeout(this.renderTimeout);
        this.renderTimeout = setTimeout(() => {
            if (typeof Worker !== 'undefined') {
                this.fnGenerateWorkerDiagram();
            } else {
                this.fnGenerateComponentDiagram();
            }
        }, 200);
    }

    fnGenerateComponentDiagram() {
        switch (this.activeTab) {
            case 'SUNBURST':
                let chartData = getSunBurstChildrenData(0, this.dataList, this.selectedParameters, []);
                this.modifySunburstChartData(chartData);
                break;
            case 'SANKEY':
                let response = {
                    nodes: [],
                    links: [],
                    groups: []
                };
                response = getSankeyData(0, this.dataList, this.selectedParameters, response);
                this.modifySankeyChartData(response);
                break;
        }
    }

    fnGenerateWorkerDiagram() {
        try {
            const worker = new Worker(`${window.location.origin}/pep/dist/assets/workers/datenubersicht.worker.js?t=${new Date().getTime()}`);

            const params = {
                index: 0,
                dataList: this.dataList,
                selectedParameters: this.selectedParameters
            };
            worker.postMessage({ ...params, type: this.activeTab });

            worker.onmessage = ({ data }) => {
                if (this.activeTab === 'SUNBURST') {
                    this.modifySunburstChartData(data);
                } else {
                    this.modifySankeyChartData(data)
                }
                worker.terminate();
            };

            worker.onerror = (err) => {
                console.error('Worker error', err);
                this.fnGenerateComponentDiagram();
                worker.terminate();
            };
        } catch (err) {
            this.fnGenerateComponentDiagram();
        }
    }

    modifySunburstChartData(data) {
        this.chartData = {
            name: 'All',
            children: data
        };
        this.displaySunBurstChart();
    }

    modifySankeyChartData(data) {
        data.links = data.links.filter(a => a.source != a.target);
        let orders = [];
        for (var i = data.groups.length - 1; i >= 0; i--) {
            let item = data.groups[i];
            let ageText = this.LANGUAGE == LANGUAGE_ENUM.EN ? 'Age' : 'Alter';
            if (item.id == ageText) {
                orders.push([`${ageText}^<21`, `${ageText}^21-40`, `${ageText}^41-65`, `${ageText}^>65`]);
            } else {
                orders.push(item.nodes.sort());
            }
        }
        data.order = orders;
        this.chartData = data;
        this.displaySankeyChart();
    }

    displaySunBurstChart() {
        document.getElementById('chart').innerHTML = "";

        let width = document.getElementById("chartContainer").clientWidth - 40; //window.innerWidth - 420;
        let height = window.innerHeight - 150;


        const color = d3.scaleOrdinal(d3.schemePaired);

        var myChart = new Sunburst(document.getElementById('chart'));
        myChart.data(this.chartData)
            .label('name')
            .size('size')
            .width(width)
            .height(height)
            //.excludeRoot(true)
            .centerRadius(0)
            .showLabels(true)
            .color(d => {
                return color(d.name)
                //let parent = d.__dataNode.parent;
                //return color(parent ? parent.data.name : null)
            })
            .tooltipContent((d: any, node: any) => {
                if (d.size >= 0) {
                    return `Size: <i>${d.size}</i>`
                } else {
                    var size = sumBy(d.children, "size");
                    return `Size: <i>${size}</i>`
                }
            });
        this.showLoader = false;

    }

    displaySankeyChart() {
        this.showLoader = true;

        const totalGroups = this.chartData.groups.length;

        if (totalGroups < 2) {
            var message = this.LANGUAGE == LANGUAGE_ENUM.EN ? "Please select at least two parameters." : "Bitte wählen Sie mindestens zwei Parameter aus.";
            document.getElementById('chart').innerHTML = `<h5 class="alert alert-warning text-center d-flex justify-content-center">${message}</h5>`;
            this.showLoader = false;
            return;
        }
        if (typeof Worker !== 'undefined') {
            this.fnDisplaySankeyChartUsingWorker();
        } else {
            this.fnDisplaySankeyChartUsingComponent();
        }
    }

    getSankeyChartDetails() {
        const chartContainer = document.getElementById("chartContainer");
        const width = chartContainer.clientWidth - 40;

        let height = window.innerHeight - 150;
        let maxVal = 0, start = 0, end = 0;
        const totalGroups = this.chartData.groups.length;

        // Precalculate layout bounds
        for (let i = 0; i < totalGroups; i++) {
            const nodes = this.chartData.groups[i].nodes;
            const maxLength = Math.max(...nodes.map(el => el.length || 0));
            maxVal = Math.max(maxVal, nodes.length);

            if (i === totalGroups - 1) start = Math.max(start, maxLength);
            if (i === 0) end = Math.max(end, maxLength);
        }

        const multiply = 6.5;
        const maxLength = 55;

        start = Math.min(start, maxLength);
        end = Math.min(end, maxLength);

        start = Math.max(start * multiply, 100);
        end = Math.max(end * multiply, 100);
        let top = 0;
        let maxValMulti = 48;
        if (maxVal * maxValMulti > height) {
            height = maxVal * maxValMulti;
            top = height / 10 - 50;
        }
        return { width, height, start, end, top };
    }

    fnDisplaySankeyChartUsingComponent() {
        let { width, height, start, end, top } = this.getSankeyChartDetails();
        const layout = d3Sankey.sankey().extent([[start, 0], [width - end, height]]);
        let order = this.chartData.order || null;
        if (order) layout.ordering(order);

        const layoutData = layout(this.chartData);
        this.fnDisplaySankeyChartSVG(layoutData);
    }

    fnDisplaySankeyChartUsingWorker() {
        try {
            const worker = new Worker(`${window.location.origin}/pep/dist/assets/workers/sankey-layout.worker.js?t=${new Date().getTime()}`);

            let { width, height, start, end } = this.getSankeyChartDetails();

            worker.postMessage({
                chartData: this.chartData,
                width,
                height,
                start,
                end,
                order: this.chartData.order || null
            });

            worker.onmessage = ({ data }) => {
                this.fnDisplaySankeyChartSVG(data);
                worker.terminate();
            };

            worker.onerror = (err) => {
                console.error('Worker error', err);
                this.fnDisplaySankeyChartUsingComponent();
                worker.terminate();
            };
        } catch (err) {
            this.fnDisplaySankeyChartUsingComponent();
        }
    }


    fnDisplaySankeyChartSVG(layoutData) {
        let component = this;
        let { width, height, top } = this.getSankeyChartDetails();
        const color = d3.scaleOrdinal(d3.schemeCategory10);

        // Title helpers
        const getTitle = d => d.title;
        const getNodeTitle = title => {
            const maxLine = 25;
            const lines = [];
            for (let i = 0; i < title.length; i += maxLine) {
                lines.push(title.slice(i, i + maxLine));
            }
            return lines.join("\n");
        };

        // Create diagram
        const diagram = d3Sankey.sankeyDiagram()
            .margins({ top: -top, right: 0, bottom: 0, left: 0 })
            .nodeTitle(d => `${d.title} (${d.value})`)
            .linkTitle(d => `${getTitle(d.source)} → ${getTitle(d.target)}\nSize: ${d.value}`)
            .linkColor(d => color(d.source.id));

        requestAnimationFrame(() => {
            document.getElementById('chart').innerHTML = "";
            d3.select('#chart')
                .append("svg")
                .attr('width', width)
                .attr('height', height - (top * 2))
                .datum(layoutData)
                .transition()
                .duration(600)
                .call(diagram.groups(component.chartData.groups));

            this.truncateSvgText();

            this.showLoader = false;
        });
    }
    truncateSvgText() {
        const maxLength = 48;
        const svgTextElements = document.querySelectorAll('svg .node-title');

        svgTextElements.forEach((textElement: any) => {
            const originalText = textElement.textContent || '';

            let value = "";
            const match = originalText.match(/\(([^)]+)\)$/);
            if (match) {
                value = match[1];
            }

            let Textlength = maxLength - (value.length + 3);
            if (originalText.length > Textlength) {
                const truncated = originalText.slice(0, Textlength - 3) + '...';
                textElement.textContent = truncated + ` (${value})`;
            }
        });
    }


    fnOpenInfoPopup() {
        let panels = jsPanel.getPanels();
        panels.forEach(panel => {
            panel.close();
        })
        let component = this;
        // Code for get html element from component
        const componentRef = this.vcr.createComponent(DatenubersichtInfoComponent);
        componentRef.instance.LANGUAGE = this.LANGUAGE;

        const element = componentRef.location.nativeElement;

        this.jsPanelPopup = jsPanel.create({
            id: "jsPanelInfo",
            theme: 'dark',
            panelSize: {
                width: () => { return component.panelWidth; },
                height: () => { return component.panelHeight; }
            },
            animateIn: 'jsPanelFadeIn',
            headerTitle: this.LANGUAGE == LANGUAGE_ENUM.EN ? 'Data overview' : 'Datenubersicht Info',
            css: {
                panel: 'datenubersicht-info-panel'
            },
            onclosed: (panel, closedByUser) => {
                this.jsPanelPopup = undefined;
            },
            content: element,
            callback: function (panel) {
            }
        });
        this.jsPanelPopup = this.sharedService.setJsPanelProperty(this.jsPanelPopup);
    }

    fnDownloadDiagram() {
        const chartElement = document.getElementById('chart');
        if (!chartElement) return;

        const svgElement = chartElement.querySelector('svg') as SVGSVGElement;
        if (!svgElement) return;

        // Inline computed styles into the SVG
        const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
        this.inlineAllStyles(svgElement, clonedSvg);

        const svgData = new XMLSerializer().serializeToString(clonedSvg);
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.activeTab.toLowerCase()} diagram.svg`;
        a.click();
        URL.revokeObjectURL(url);
    }

    inlineAllStyles(sourceSVG: SVGSVGElement, targetSVG: SVGSVGElement) {
        const allElements = sourceSVG.querySelectorAll('*');
        const targetElements = targetSVG.querySelectorAll('*');

        allElements.forEach((el, i) => {
            const computedStyle = getComputedStyle(el);
            const target = targetElements[i] as HTMLElement;
            const style = Array.from(computedStyle)
                .map(prop => `${prop}:${computedStyle.getPropertyValue(prop)};`)
                .join('');
            target.setAttribute('style', style);
        });
    }
}
